import { annotateImage } from "./capture/annotate";
import { AudioRecorder } from "./capture/audio";
import { ScreenRecorder } from "./capture/screen-recording";
import { captureDOMScreenshot } from "./capture/screenshot";
import { buildReportPayload } from "./payload";
import { widgetStyles } from "./styles";
import { IngestError, submitReport } from "./transport";

type Status = { tone: "info" | "error" | "success"; text: string } | null;

const ICON_BUG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2l1.5 2.5"/><path d="M16 2l-1.5 2.5"/><rect x="6" y="7" width="12" height="13" rx="6"/><path d="M12 7v13"/><path d="M3 13h3"/><path d="M18 13h3"/><path d="M4 8l3 2"/><path d="M20 8l-3 2"/><path d="M4 18l3-2"/><path d="M20 18l-3-2"/></svg>`;
const ICON_CAMERA = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="4"/></svg>`;
const ICON_MIC =`<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 11a7 7 0 0 0 14 0"/><path d="M12 18v3"/></svg>`;
const ICON_X = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>`;
const ICON_PEN = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>`;

export class ArkSnitchElement extends HTMLElement {
  static get observedAttributes() {
    return ["position"];
  }

  private root: ShadowRoot;
  private modalOpen = false;
  private screenshotFile: File | null = null;
  private screenshotUrl: string | null = null;
  private voiceFile: File | null = null;
  private voiceUrl: string | null = null;
  private screenRecordingFile: File | null = null;
  private screenRecordingUrl: string | null = null;
  private voiceRecorder: AudioRecorder | null = null;
  private voiceRecordingMs = 0;
  private screenRecorder: ScreenRecorder | null = null;
  private screenRecordingMs = 0;
  private submitting = false;
  private annotating = false;
  private status: Status = null;
  private titleValue = "";
  private noteValue = "";
  private panelLeft: number | null = null;
  private panelTop: number | null = null;
  private dragState: {
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startPanelX: number;
    startPanelY: number;
  } | null = null;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: "open" });
  }

  private boundKeydown = (e: KeyboardEvent) => {
    if (this.modalOpen && e.key === "Escape") this.close();
  };

  connectedCallback() {
    this.render();
    document.addEventListener("keydown", this.boundKeydown);
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.boundKeydown);
    this.revokePreviews();
  }

  attributeChangedCallback() {
    if (this.isConnected) this.render();
  }

  private get endpoint(): string {
    return this.getAttribute("endpoint")?.trim() || "";
  }
  private get apiKey(): string {
    return this.getAttribute("api-key")?.trim() || "";
  }
  private get position(): string {
    return this.getAttribute("position") || "bottom-right";
  }
  private get maskSelector(): string {
    return this.getAttribute("mask-selector") || "[data-mask]";
  }
  private get maxRecordingMs(): number {
    const s = parseInt(this.getAttribute("max-recording-seconds") || "60", 10);
    return Number.isFinite(s) && s > 0 ? s * 1000 : 60_000;
  }
  private get maxScreenRecordingMs(): number {
    const s = parseInt(
      this.getAttribute("max-screen-recording-seconds") || "120",
      10,
    );
    return Number.isFinite(s) && s > 0 ? s * 1000 : 120_000;
  }
  private get hideButton(): boolean {
    return this.hasAttribute("hide-button");
  }

  open() {
    if (!this.endpoint || !this.apiKey) {
      console.warn(
        "[ark-snitch] missing required attributes 'endpoint' and/or 'api-key'",
      );
    }
    this.modalOpen = true;
    this.status = null;
    this.render();
  }

  close() {
    this.modalOpen = false;
    this.voiceRecorder?.stop();
    this.screenRecorder?.stop();
    this.render();
  }

  private resetReport() {
    this.revokePreviews();
    this.screenshotFile = null;
    this.voiceFile = null;
    this.screenRecordingFile = null;
    this.voiceRecordingMs = 0;
    this.screenRecordingMs = 0;
    this.voiceRecorder = null;
    this.screenRecorder = null;
    this.titleValue = "";
    this.noteValue = "";
  }

  private revokePreviews() {
    if (this.screenshotUrl) URL.revokeObjectURL(this.screenshotUrl);
    if (this.voiceUrl) URL.revokeObjectURL(this.voiceUrl);
    if (this.screenRecordingUrl) URL.revokeObjectURL(this.screenRecordingUrl);
    this.screenshotUrl = null;
    this.voiceUrl = null;
    this.screenRecordingUrl = null;
  }

  private async handleCaptureDOM() {
    this.status = { tone: "info", text: "Capturing screenshot…" };
    this.render();
    try {
      const file = await captureDOMScreenshot({ maskSelector: this.maskSelector });
      this.setScreenshot(file);
      this.status = null;
    } catch (err) {
      this.status = { tone: "error", text: `Capture failed: ${(err as Error).message}` };
    }
    this.render();
  }

  private async handleAnnotate() {
    if (!this.screenshotFile || this.annotating) return;
    this.annotating = true;
    this.status = { tone: "info", text: "Opening annotator…" };
    this.render();
    // Hide the widget host so markerjs2's popup owns the screen — avoids
    // fighting our z-index ceiling.
    const prevDisplay = this.style.display;
    this.style.display = "none";
    try {
      const file = await annotateImage(this.screenshotFile);
      this.setScreenshot(file);
      this.status = null;
    } catch (err) {
      this.status = { tone: "error", text: `Annotate failed: ${(err as Error).message}` };
    } finally {
      this.style.display = prevDisplay;
      this.annotating = false;
    }
    this.render();
  }

  private setScreenshot(file: File) {
    if (this.screenshotUrl) URL.revokeObjectURL(this.screenshotUrl);
    this.screenshotFile = file;
    this.screenshotUrl = URL.createObjectURL(file);
  }

  private setVoice(file: File) {
    if (this.voiceUrl) URL.revokeObjectURL(this.voiceUrl);
    this.voiceFile = file;
    this.voiceUrl = URL.createObjectURL(file);
  }

  private setScreenRecording(file: File) {
    if (this.screenRecordingUrl) URL.revokeObjectURL(this.screenRecordingUrl);
    this.screenRecordingFile = file;
    this.screenRecordingUrl = URL.createObjectURL(file);
  }

  private async handleToggleVoiceRecord() {
    if (this.voiceRecorder?.isRecording()) {
      this.voiceRecorder.stop();
      return;
    }
    if (!AudioRecorder.isSupported()) {
      this.status = { tone: "error", text: "Audio recording not supported here" };
      this.render();
      return;
    }
    this.voiceRecorder = new AudioRecorder({
      maxDurationMs: this.maxRecordingMs,
      onTick: (ms) => {
        this.voiceRecordingMs = ms;
        this.updateChipLabel("toggle-voice", this.voiceChipLabel());
      },
      onStop: (file) => {
        this.voiceRecorder = null;
        this.voiceRecordingMs = 0;
        this.setVoice(file);
        this.render();
      },
      onError: (err) => {
        this.voiceRecorder = null;
        this.voiceRecordingMs = 0;
        this.status = { tone: "error", text: `Recording error: ${err.message}` };
        this.render();
      },
    });
    try {
      await this.voiceRecorder.start();
      this.status = null;
    } catch (err) {
      this.voiceRecorder = null;
      this.status = {
        tone: "error",
        text: `Mic permission denied: ${(err as Error).message}`,
      };
    }
    this.render();
  }

  private async handleToggleScreenRecord() {
    if (this.screenRecorder?.isRecording()) {
      this.screenRecorder.stop();
      return;
    }
    if (!ScreenRecorder.isSupported()) {
      this.status = { tone: "error", text: "Screen recording not supported here" };
      this.render();
      return;
    }
    this.screenRecorder = new ScreenRecorder({
      maxDurationMs: this.maxScreenRecordingMs,
      onTick: (ms) => {
        this.screenRecordingMs = ms;
        this.updateChipLabel("toggle-screen", this.screenChipLabel());
      },
      onStop: (file) => {
        this.screenRecorder = null;
        this.screenRecordingMs = 0;
        this.setScreenRecording(file);
        this.render();
      },
      onError: (err) => {
        this.screenRecorder = null;
        this.screenRecordingMs = 0;
        this.status = { tone: "error", text: `Screen recording error: ${err.message}` };
        this.render();
      },
    });
    this.status = { tone: "info", text: "Pick a window or screen…" };
    this.render();
    try {
      await this.screenRecorder.start();
      this.status = null;
    } catch (err) {
      this.screenRecorder = null;
      this.status = {
        tone: "error",
        text: `Screen capture denied: ${(err as Error).message}`,
      };
    }
    this.render();
  }

  private voiceChipLabel(): string {
    const recording = this.voiceRecorder?.isRecording() ?? false;
    const secs = Math.floor(this.voiceRecordingMs / 1000);
    return recording ? `Stop ${secs}s` : this.voiceFile ? "Re-record" : "Record voice";
  }

  private screenChipLabel(): string {
    const recording = this.screenRecorder?.isRecording() ?? false;
    const secs = Math.floor(this.screenRecordingMs / 1000);
    return recording ? `Stop ${secs}s` : this.screenRecordingFile ? "Re-record" : "Record screen";
  }

  private async handleSubmit() {
    if (this.submitting) return;
    if (!this.endpoint || !this.apiKey) {
      this.status = { tone: "error", text: "Widget not configured (endpoint / api-key)" };
      this.render();
      return;
    }
    this.syncFormState();
    const title = this.titleValue.trim();
    const note = this.noteValue.trim();
    if (!title) {
      this.status = { tone: "error", text: "A short title helps the inspector triage" };
      this.render();
      const titleEl = this.root.getElementById("snitch-title") as HTMLInputElement | null;
      titleEl?.focus();
      return;
    }

    this.submitting = true;
    this.status = { tone: "info", text: "Sending…" };
    this.render();

    try {
      const payload = buildReportPayload();
      await submitReport({
        endpoint: this.endpoint,
        apiKey: this.apiKey,
        title,
        note,
        payload,
        screenshot: this.screenshotFile,
        voiceNote: this.voiceFile,
        screenRecording: this.screenRecordingFile,
      });
      this.status = { tone: "success", text: "Report sent — thanks." };
      this.submitting = false;
      this.render();
      window.setTimeout(() => {
        this.resetReport();
        this.modalOpen = false;
        this.status = null;
        this.render();
      }, 1400);
    } catch (err) {
      this.submitting = false;
      const msg =
        err instanceof IngestError
          ? `Submit failed (${err.status}): ${err.body || err.message}`
          : `Submit failed: ${(err as Error).message}`;
      this.status = { tone: "error", text: msg };
      this.render();
    }
  }

  private syncFormState() {
    const titleEl = this.root.getElementById("snitch-title") as HTMLInputElement | null;
    const noteEl = this.root.getElementById("snitch-note") as HTMLTextAreaElement | null;
    if (titleEl) this.titleValue = titleEl.value;
    if (noteEl) this.noteValue = noteEl.value;
  }

  // Targeted update for a recording chip — used by tick callbacks so we
  // don't full re-render the modal 5x/sec while recording (which would tear
  // down + rebuild the entire shadow DOM and visibly flash). Updates the
  // [data-recording]/[data-active] flags + trailing label text in place.
  private updateChipLabel(action: string, label: string) {
    if (!this.modalOpen) return;
    const chip = this.root.querySelector<HTMLButtonElement>(
      `[data-action="${action}"]`,
    );
    if (!chip) return;
    if (action === "toggle-voice") {
      chip.setAttribute(
        "data-recording",
        String(this.voiceRecorder?.isRecording() ?? false),
      );
      chip.setAttribute("data-active", String(!!this.voiceFile));
    } else if (action === "toggle-screen") {
      chip.setAttribute(
        "data-recording",
        String(this.screenRecorder?.isRecording() ?? false),
      );
      chip.setAttribute("data-active", String(!!this.screenRecordingFile));
    }
    const svg = chip.querySelector("svg");
    if (!svg) return;
    let node: ChildNode | null = svg.nextSibling;
    while (node && node.nodeType !== Node.TEXT_NODE) node = node.nextSibling;
    if (node) {
      node.textContent = ` ${label}`;
    } else {
      chip.appendChild(document.createTextNode(` ${label}`));
    }
  }

  private escapeAttr(s: string): string {
    return s
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;");
  }

  private escapeText(s: string): string {
    return s.replace(/&/g, "&amp;").replace(/</g, "&lt;");
  }

  private render() {
    this.syncFormState();
    const voiceRecording = this.voiceRecorder?.isRecording() ?? false;
    const voiceLabel = this.voiceChipLabel();

    this.root.innerHTML = `
      <style>${widgetStyles}</style>
      ${
        this.hideButton || this.modalOpen
          ? ""
          : `<button class="toggle" data-position="${this.position}" aria-label="Report a bug" data-action="open">${ICON_BUG}</button>`
      }
      ${
        this.modalOpen
          ? `
        <div class="panel" role="dialog" aria-label="Report a bug"${
          this.panelLeft != null && this.panelTop != null
            ? ` style="left:${this.panelLeft}px;top:${this.panelTop}px;right:auto;bottom:auto;"`
            : ""
        }>
          <div class="header">
              <div class="title-row" data-drag-handle title="Drag to move">
                <span class="dot"></span>
                <h2>Report a bug</h2>
              </div>
              <button class="close" aria-label="Close" data-action="close">${ICON_X}</button>
            </div>
            <div class="body">
              <label class="field">
                <span>Title</span>
                <input id="snitch-title" type="text" placeholder="Short summary" value="${this.escapeAttr(this.titleValue)}" />
              </label>
              <label class="field">
                <span>What happened?</span>
                <textarea id="snitch-note" placeholder="Steps, expected vs actual, anything else useful…">${this.escapeText(this.noteValue)}</textarea>
              </label>

              <div class="actions-row">
                <button class="chip" data-action="capture-dom" data-active="${!!this.screenshotFile}">${ICON_CAMERA} DOM</button>
                <button class="chip" data-action="toggle-voice" data-recording="${voiceRecording}" data-active="${!!this.voiceFile}">${ICON_MIC} ${voiceLabel}</button>
              </div>

              ${
                this.screenshotUrl
                  ? `<div class="preview">
                       <div class="meta">
                         <span>Screenshot · ${Math.round((this.screenshotFile?.size ?? 0) / 1024)} KB</span>
                         <div class="meta-actions">
                           <button class="meta-btn" data-action="annotate-screenshot" ${this.annotating ? "disabled" : ""}>${ICON_PEN} ${this.annotating ? "Opening…" : "Annotate"}</button>
                           <button class="unset" data-action="clear-screenshot">remove</button>
                         </div>
                       </div>
                       <img src="${this.screenshotUrl}" alt="screenshot preview" />
                     </div>`
                  : ""
              }

              ${
                this.screenRecordingUrl
                  ? `<div class="preview">
                       <div class="meta">
                         <span>Screen recording · ${Math.round((this.screenRecordingFile?.size ?? 0) / 1024)} KB</span>
                         <button class="unset" data-action="clear-screen-recording">remove</button>
                       </div>
                       <video controls src="${this.screenRecordingUrl}" preload="metadata"></video>
                     </div>`
                  : ""
              }

              ${
                this.voiceUrl
                  ? `<div class="preview">
                       <div class="meta">
                         <span>Voice note · ${Math.round((this.voiceFile?.size ?? 0) / 1024)} KB</span>
                         <button class="unset" data-action="clear-voice">remove</button>
                       </div>
                       <audio controls src="${this.voiceUrl}"></audio>
                     </div>`
                  : ""
              }

              ${
                this.status
                  ? `<div class="status" data-tone="${this.status.tone}">${this.status.text}</div>`
                  : ""
              }
            </div>

            <div class="footer">
              <button class="btn btn-ghost" data-action="close">Cancel</button>
              <button class="btn btn-primary" data-action="submit" ${this.submitting ? "disabled" : ""}>
                ${this.submitting ? "Sending…" : "Submit report"}
              </button>
            </div>
        </div>
      `
          : ""
      }
    `;

    this.root.querySelectorAll<HTMLElement>("[data-action]").forEach((el) => {
      el.addEventListener("click", (e) => this.dispatch(el.dataset.action!, e));
    });

    const handle = this.root.querySelector<HTMLElement>("[data-drag-handle]");
    if (handle) {
      handle.addEventListener("pointerdown", this.onDragStart);
    }
  }

  private onDragStart = (e: PointerEvent) => {
    if (e.button !== 0) return; // primary button only
    const handle = e.currentTarget as HTMLElement;
    const panel = this.root.querySelector<HTMLElement>(".panel");
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    // Switch from right/bottom defaults to absolute left/top so move math is direct.
    panel.style.left = `${rect.left}px`;
    panel.style.top = `${rect.top}px`;
    panel.style.right = "auto";
    panel.style.bottom = "auto";
    this.dragState = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startPanelX: rect.left,
      startPanelY: rect.top,
    };
    handle.setAttribute("data-dragging", "true");
    handle.setPointerCapture(e.pointerId);
    handle.addEventListener("pointermove", this.onDragMove);
    handle.addEventListener("pointerup", this.onDragEnd);
    handle.addEventListener("pointercancel", this.onDragEnd);
    e.preventDefault();
  };

  private onDragMove = (e: PointerEvent) => {
    const s = this.dragState;
    if (!s || e.pointerId !== s.pointerId) return;
    const panel = this.root.querySelector<HTMLElement>(".panel");
    if (!panel) return;
    const dx = e.clientX - s.startClientX;
    const dy = e.clientY - s.startClientY;
    const maxX = window.innerWidth - panel.offsetWidth;
    const maxY = window.innerHeight - panel.offsetHeight;
    const x = Math.max(0, Math.min(maxX, s.startPanelX + dx));
    const y = Math.max(0, Math.min(maxY, s.startPanelY + dy));
    panel.style.left = `${x}px`;
    panel.style.top = `${y}px`;
  };

  private onDragEnd = (e: PointerEvent) => {
    const s = this.dragState;
    if (!s || e.pointerId !== s.pointerId) return;
    const handle = e.currentTarget as HTMLElement;
    const panel = this.root.querySelector<HTMLElement>(".panel");
    if (panel) {
      // Commit final position to instance state so subsequent renders preserve it.
      this.panelLeft = parseFloat(panel.style.left || "0");
      this.panelTop = parseFloat(panel.style.top || "0");
    }
    this.dragState = null;
    handle.removeAttribute("data-dragging");
    try {
      handle.releasePointerCapture(s.pointerId);
    } catch {
      /* already released */
    }
    handle.removeEventListener("pointermove", this.onDragMove);
    handle.removeEventListener("pointerup", this.onDragEnd);
    handle.removeEventListener("pointercancel", this.onDragEnd);
  };

  private dispatch(action: string, e: Event) {
    switch (action) {
      case "open":
        this.open();
        break;
      case "close":
        this.close();
        break;
      case "capture-dom":
        void this.handleCaptureDOM();
        break;
      case "toggle-screen":
        void this.handleToggleScreenRecord();
        break;
      case "toggle-voice":
        void this.handleToggleVoiceRecord();
        break;
      case "annotate-screenshot":
        void this.handleAnnotate();
        break;
      case "clear-screenshot":
        if (this.screenshotUrl) URL.revokeObjectURL(this.screenshotUrl);
        this.screenshotFile = null;
        this.screenshotUrl = null;
        this.render();
        break;
      case "clear-screen-recording":
        if (this.screenRecordingUrl) URL.revokeObjectURL(this.screenRecordingUrl);
        this.screenRecordingFile = null;
        this.screenRecordingUrl = null;
        this.render();
        break;
      case "clear-voice":
        if (this.voiceUrl) URL.revokeObjectURL(this.voiceUrl);
        this.voiceFile = null;
        this.voiceUrl = null;
        this.render();
        break;
      case "submit":
        void this.handleSubmit();
        break;
    }
  }
}
